import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { RestfulEndpoint } from '../restful-endpoint.service';
import {
  LastPhaseResponse,
  WeeklyReportComment,
  WeeklyReportCommentResponse,
  WeeklyReportDetailResponse,
  WeeklyReportMentionableUsersResponse,
  WeeklyReportResponse,
} from '../../models/weeklyReports.model';

@Injectable({
  providedIn: 'root',
})
export class WeeklyReportService {
  constructor(
    private endPoint: RestfulEndpoint, // Inject the RestfulEndpoint service
  ) {}

  private _pageIndex = new BehaviorSubject<number>(0);
  pageIndex$ = this._pageIndex.asObservable();

  // Properties to track pagination information

  /**
   * Fetch weekly reports from the backend
   * @param projectId - The ID of the project to fetch reports for
   * @param currentPage - The current page number (optional)
   * @param per_page - The number of reports to fetch per page (optional)
   * @returns An observable of the weekly report response
   */
  getWeeklyReports(
    projectId: string,
    currentPage?: number,
    per_page?: number,
  ): Observable<WeeklyReportResponse> {
    return this.endPoint.get(`projects/${projectId}/weekly-reports`, {
      page: currentPage,
      per_page: per_page,
    });
  }

  getWeeklyReportDetails(
    projectId: string,
    reportId: string,
  ): Observable<WeeklyReportDetailResponse> {
    return this.endPoint.get(`projects/${projectId}/weekly-reports/${reportId}`);
  }

  getWeeklyReportComments(projectId: string, reportId: string): Observable<any> {
    return this.endPoint.get(`projects/${projectId}/weekly-reports/${reportId}/comments`);
  }

  /**
   * Create a new weekly report
   * @param body - The request body for the new weekly report
   * @returns An observable of the response
   */
  createWeeklyReport(body: any, projectId: string): Observable<any> {
    return this.endPoint.post(`v2/projects/${projectId}/weekly-reports`, body);
  }

  updateWeeklyReport(body: any, projectId: string, reportId: string): Observable<any> {
    return this.endPoint.post(`v2/projects/${projectId}/weekly-reports/${reportId}`, body);
  }

  deleteWeeklyReport(projectId: string, reportId: string): Observable<any> {
    return this.endPoint.delete(`v2/projects/${projectId}/weekly-reports/${reportId}`);
  }

  getLastPhaseDetails(projectId: string): Observable<LastPhaseResponse> {
    return this.endPoint.get(`projects/${projectId}/last-phase`);
  }

  /**
   * Update the query parameters in the URL
   * This should be called when the user interacts with the paginator
   */

  resetReportPaginatorPageIndex() {
    this._pageIndex.next(0);
  }

  createWeeklyReportReply(
    body: any,
    projectId: string,
    reportId: string,
  ): Observable<WeeklyReportCommentResponse> {
    return this.endPoint.post(`v2/projects/${projectId}/weekly-reports/${reportId}/comments`, body);
  }

  getMentionableUsers(
    projectId: string,
    search_keyword: string,
  ): Observable<WeeklyReportMentionableUsersResponse> {
    return this.endPoint.get(
      `projects/${projectId}/weekly-reports/mentionable-users?search_keyword=${search_keyword}`,
    );
  }
}
